import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import pendingActionService from '../services/pendingActionService';
import { trackLandingView, trackCTAClick, trackSignupStart, trackSignupComplete, trackLogin } from '../utils/analytics';
import { getPostAuthRedirect } from '../utils/authRedirect';
import SEO from './SEO';
import PersonaCrossSell from './PersonaCrossSell';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './PersonaLanding.css';
import './CoachingLanding.css';

const CoachingLanding = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);

  useEffect(() => { trackLandingView('coaching'); }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('coaching');
    try {
      if (!credentialResponse.credential) { setAuthError('Missing authentication credentials'); return; }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      if (!response.ok) throw new Error(`Authentication failed: ${response.status}`);
      const data = await response.json();
      const userData = { ...data.user, sub: data.user.sub || data.user.id || data.user.email };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      if (data.isNewUser) {
        trackSignupComplete('coaching', userData.sub || userData.email);
      } else {
        trackLogin('coaching', userData.sub || userData.email);
      }
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: '/affiliate/register'
      });
      if (redirectUrl) toast.success('Welcome!');
      window.location.href = next;
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  const checkSvg = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
  );

  const seo = PAGE_SEO['/coaching'];

  return (
    <div className="persona-landing coaching">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/coaching"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Online Fitness & Nutrition Coaching',
            provider: {
              '@type': 'Organization',
              name: 'FreshFitLab',
              url: 'https://freshfitlab.io',
            },
            areaServed: 'Worldwide',
            serviceType: 'Online personal training and nutrition coaching',
            description: seo.description,
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Coaching', url: '/coaching' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do I find a coach on FreshFitLab?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Browse the Coaches directory, view each coach\u2019s profile, specializations, programs, and pricing, then book directly inside the app.',
                },
              },
              {
                '@type': 'Question',
                name: 'How do coaches assign programs and meal plans?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Once connected with a coach they can assign workouts, programs, and nutrition plans straight to your schedule with progress tracking.',
                },
              },
              {
                '@type': 'Question',
                name: 'How are coaches paid?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Coaches collect payments through Stripe Connect, integrated directly into FreshFitLab. They keep most of the revenue and FreshFitLab takes a small platform fee.',
                },
              },
            ],
          },
        ]}
      />
      <div className="pl-bg-shapes">
        <div className="pl-shape pl-shape-1"></div>
        <div className="pl-shape pl-shape-2"></div>
        <div className="pl-shape pl-shape-3"></div>
      </div>

      {/* ─── Hero ─── */}
      <section className="pl-hero">
        <span className="pl-hero-badge">Grow Your Coaching Business</span>
        <h1>
          Train Clients,<br />
          <span className="pl-highlight">Earn on Your Terms</span>
        </h1>
        <p className="pl-hero-subtitle">
          Build nutrition plans, create workout programs, manage clients, and collect payments via Stripe —
          all from a single platform designed for coaches.
        </p>

        <div className="pl-hero-ctas">
          <button className="pl-cta-primary" onClick={() => { trackCTAClick('coaching', 'hero'); navigate('/affiliate/register'); }}>
            <span>Get Started as a Coach</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('coaching', 'hero_secondary'); navigate('/pricing#pricing-creator'); }}>
            <span>View Pricing</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <div className="pl-login-divider"><span>or</span></div>
        <div className="pl-login-section">
          <p className="pl-login-label">Already a coach? Sign in</p>
          {process.env.NODE_ENV === 'development' ? (
            <button onClick={() => navigate('/dev/login')} className="pl-dev-login-button">Dev Login</button>
          ) : (
            <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={() => setAuthError('Login Failed')} text="signin" shape="pill" />
          )}
          {authError && <p className="pl-auth-error">{authError}</p>}
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="pl-steps">
        <h2 className="pl-steps-title">How It Works</h2>
        <div className="pl-steps-grid">
          <div className="pl-step">
            <div className="pl-step-number">1</div>
            <h3>Create Your Profile</h3>
            <p>Sign up as a creator, set up your coach profile, and choose your specialties.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">2</div>
            <h3>Build Programs &amp; Plans</h3>
            <p>Publish menus, create workout programs, and assign nutrition plans to your clients.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">3</div>
            <h3>Get Paid via Stripe</h3>
            <p>Collect coaching payments directly through Stripe. Track earnings and client analytics.</p>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="pl-features">
        <h2 className="pl-features-title">Everything You Need to Coach</h2>
        <div className="pl-features-grid">
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-clients"><i className="fas fa-users"></i></div>
            <h3>Client Management</h3>
            <p>Invite clients, view their progress, and manage all your coaching relationships in one place.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-nutrition"><i className="fas fa-apple-whole"></i></div>
            <h3>Nutrition Plans</h3>
            <p>Create and assign personalised meal plans with calorie and macro targets for each client.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-workouts"><i className="fas fa-dumbbell"></i></div>
            <h3>Workout Programming</h3>
            <p>Build multi-week programs with exercises, sets, reps, and progressive overload built in.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-payments"><i className="fas fa-credit-card"></i></div>
            <h3>Stripe Payments &amp; Analytics</h3>
            <p>Collect coaching fees via Stripe. Track earnings, commissions, and client engagement.</p>
          </div>
        </div>
      </section>

      {/* ─── Earnings Visual ─── */}
      <section className="pl-split">
        <div className="pl-split-text">
          <h2>Earn While You Coach</h2>
          <p>
            Whether you're sharing recipes or training clients one-on-one, FreshFitLab gives you the
            tools to monetise your expertise.
          </p>
          <ul className="pl-perks">
            <li>{checkSvg}<span>Publish menus &amp; workout programs</span></li>
            <li>{checkSvg}<span>Earn commission on Instacart orders</span></li>
            <li>{checkSvg}<span>Collect coaching payments via Stripe</span></li>
            <li>{checkSvg}<span>Manage clients with nutrition &amp; fitness plans</span></li>
            <li>{checkSvg}<span>Track your earnings and analytics</span></li>
          </ul>
          <Link to="/pricing#pricing-creator" className="pl-pricing-link">View Pricing Plans →</Link>
        </div>
        <div>
          <div className="pl-earnings-card">
            <div className="pl-earnings-header">
              <span className="pl-earnings-label">Your Earnings</span>
              <span className="pl-earnings-period">This Month</span>
            </div>
            <div className="pl-earnings-amount">$247.50</div>
            <div className="pl-earnings-chart">
              <div className="pl-chart-bar" style={{ height: '40%' }}></div>
              <div className="pl-chart-bar" style={{ height: '65%' }}></div>
              <div className="pl-chart-bar" style={{ height: '50%' }}></div>
              <div className="pl-chart-bar" style={{ height: '80%' }}></div>
              <div className="pl-chart-bar active" style={{ height: '95%' }}></div>
            </div>
            <div className="pl-earnings-stats">
              <div><span className="pl-stat-value">$180</span><span className="pl-stat-label">Coaching</span></div>
              <div><span className="pl-stat-value">$67</span><span className="pl-stat-label">Orders</span></div>
              <div><span className="pl-stat-value">8</span><span className="pl-stat-label">Clients</span></div>
              <div><span className="pl-stat-value">2.4k</span><span className="pl-stat-label">Views</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="pl-final-cta">
        <h2>Ready to grow your coaching business?</h2>
        <p>Join coaches already earning and building their client base on FreshFitLab.</p>
        <div className="pl-final-cta-buttons">
          <button className="pl-cta-primary pl-cta-dark" onClick={() => { trackCTAClick('coaching', 'footer'); navigate('/affiliate/register'); }}>
            <span>Get Started as a Coach</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('coaching', 'footer_secondary'); navigate('/pricing#pricing-creator'); }} style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#fff', background: 'rgba(255,255,255,0.1)' }}>
            <span>View Pricing</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </section>

      <PersonaCrossSell current="coaching" />
    </div>
  );
};

export default CoachingLanding;
